import {
  ArdoqId,
  DocumentReference,
  DocumentResourceType,
} from '@ardoq/api-types';
import { assistantApi, attachmentApi } from '@ardoq/api';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  toAttachmentIcon,
  toAttachmentType,
} from '../../documentArchive/attachmentPreview';
import {
  MAX_FILE_SIZE,
  MaxFileSizeMB,
} from '../../documentArchive/attachmentUploadUtils';
import { MAX_DOCUMENTS_LIMIT } from '../constants';
import type {
  DocumentItemViewModel,
  UploadError,
  UploadingDocument,
} from './types';

const SUPPORTED_DOCUMENT_CONTENT_TYPES: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'text/markdown',
  'text/csv',
  'text/html',
  'text/plain',
  'application/json',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

const SUPPORTED_DOCUMENT_FILE_EXTENSIONS: readonly string[] = [
  'pdf',
  'jpeg',
  'jpg',
  'png',
  'svg',
  'md',
  'csv',
  'html',
  'txt',
  'json',
  'docx',
  'xlsx',
  'pptx',
];

export type SupportedDocumentTypes = {
  contentTypes: ReadonlySet<string>;
  fileExtensions: ReadonlySet<string>;
  acceptedFileTypes: string;
};

const toAcceptedFileTypes = (
  contentTypes: readonly string[],
  fileExtensions: readonly string[]
): string =>
  [...contentTypes, ...fileExtensions.map(extension => `.${extension}`)].join(
    ','
  );

export const SUPPORTED_DOCUMENT_TYPES: SupportedDocumentTypes = {
  contentTypes: new Set(SUPPORTED_DOCUMENT_CONTENT_TYPES),
  fileExtensions: new Set(SUPPORTED_DOCUMENT_FILE_EXTENSIONS),
  acceptedFileTypes: toAcceptedFileTypes(
    SUPPORTED_DOCUMENT_CONTENT_TYPES,
    SUPPORTED_DOCUMENT_FILE_EXTENSIONS
  ),
};

const getFileExtension = (name: string): string => {
  const dotIndex = name.lastIndexOf('.');
  return dotIndex === -1 ? '' : name.slice(dotIndex + 1).toLowerCase();
};

export const isSupportedDocument = ({
  name,
  type,
}: Pick<DocumentReference, 'name' | 'type'>): boolean => {
  const { contentTypes, fileExtensions } = SUPPORTED_DOCUMENT_TYPES;
  return contentTypes.has(type) || fileExtensions.has(getFileExtension(name));
};

export type DocumentResourceLocation =
  | { resourceType: 'org'; orgId: ArdoqId }
  | { resourceType: 'user'; userId: ArdoqId };

export const toDocumentResourceLocation = (
  resourceType: DocumentResourceType,
  { orgId, userId }: { orgId: ArdoqId; userId: ArdoqId | null }
): DocumentResourceLocation | null => {
  if (resourceType === 'org') {
    return { resourceType: 'org', orgId };
  }
  if (!userId) {
    return null;
  }
  return { resourceType: 'user', userId };
};

export const getDocumentItemUrl = (
  documentId: ArdoqId,
  location: DocumentResourceLocation
): string => {
  switch (location.resourceType) {
    case 'org':
      return attachmentApi.getItemFromOrgUrl(location.orgId, documentId);
    case 'user':
      return assistantApi.getItemFromUserUrl(location.userId, documentId);
  }
};

export const toDocumentReference = ({
  id,
  name,
  type,
  resourceType,
}: DocumentItemViewModel): DocumentReference => ({
  id,
  name,
  type,
  resourceType,
});

const toChipViewModel = (
  { id, name, type, resourceType }: DocumentReference,
  {
    thumbnailUrl,
    isUploading = false,
  }: {
    thumbnailUrl: string | null;
    isUploading?: boolean;
  }
): DocumentItemViewModel => ({
  id,
  name,
  type,
  resourceType,
  icon: toAttachmentIcon(toAttachmentType(type)),
  thumbnailUrl,
  isSupported: isSupportedDocument({ name, type }),
  ...(isUploading ? { isUploading: true } : {}),
});

export const toDocumentChip = (
  documentReference: DocumentReference,
  ids: { orgId: ArdoqId; userId: ArdoqId | null }
): DocumentItemViewModel => {
  const { id, type, resourceType } = documentReference;
  const isImage = toAttachmentType(type) === 'image';
  const location = toDocumentResourceLocation(resourceType, ids);
  return toChipViewModel(documentReference, {
    thumbnailUrl: isImage && location ? getDocumentItemUrl(id, location) : null,
  });
};

export const toUploadingChip = ({
  uploadId,
  name,
  type,
}: UploadingDocument): DocumentItemViewModel =>
  toChipViewModel(
    { id: uploadId, name, type, resourceType: 'user' },
    { thumbnailUrl: null, isUploading: true }
  );

export const getUploadableFiles = ({
  files,
  availableSlots,
}: {
  files: File[];
  availableSlots: number;
}): {
  accepted: File[];
  uploadError: UploadError | null;
} => {
  const supported: File[] = [];
  const unsupported: File[] = [];
  for (const file of files) {
    const bucket = isSupportedDocument({
      name: file.name,
      type: file.type,
    })
      ? supported
      : unsupported;
    bucket.push(file);
  }
  const tooLarge = supported.filter(file => file.size > MAX_FILE_SIZE);
  const withinSize = supported.filter(file => file.size <= MAX_FILE_SIZE);
  const accepted = withinSize.slice(0, Math.max(0, availableSlots));
  const hasOverflow = accepted.length < withinSize.length;

  if (unsupported.length > 0) {
    const [firstUnsupported] = unsupported;
    const extension = getFileExtension(firstUnsupported.name);
    return {
      accepted,
      uploadError: {
        kind: 'unsupportedType',
        fileType: extension ? extension.toUpperCase() : firstUnsupported.type,
      },
    };
  }
  if (tooLarge.length > 0) {
    return {
      accepted,
      uploadError: { kind: 'tooLarge', fileNames: tooLarge.map(f => f.name) },
    };
  }
  if (hasOverflow) {
    return { accepted, uploadError: { kind: 'limitReached' } };
  }
  return { accepted, uploadError: null };
};

export const getUploadErrorMessage = (
  i18nLocale: I18nLocale,
  uploadError: UploadError
): string => {
  switch (uploadError.kind) {
    case 'unsupportedType':
      return t(
        i18nLocale,
        'assistant:documents.upload.unsupportedType',
        { fileType: uploadError.fileType },
        "{{fileType}} files aren't supported. Try a PDF, image, or CSV file."
      );
    case 'tooLarge':
      return t(
        i18nLocale,
        'assistant:documents.upload.tooLarge',
        {
          maxSize: `${MaxFileSizeMB} MB`,
          fileNames: uploadError.fileNames.join(', '),
        },
        'One or more files exceed the {{maxSize}} size limit: {{fileNames}}'
      );
    case 'limitReached':
      return t(
        i18nLocale,
        'assistant:documents.upload.limitReached',
        { count: MAX_DOCUMENTS_LIMIT },
        "You've reached the {{count}}-document limit. Remove a document to add more."
      );
    case 'uploadFailed':
      return t(
        i18nLocale,
        'assistant:documents.upload.failed',
        { fileNames: uploadError.fileNames.join(', ') },
        'Failed to upload {{fileNames}}. Please try again.'
      );
  }
};
